import {
    Activity,
    AlertTriangle,
    Target,
    Repeat,
    Crosshair,
    Gauge,
    CornerUpLeft,
    ShieldCheck,
} from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

const ICONS = [
    Activity,
    AlertTriangle,
    Target,
    Repeat,
    Crosshair,
    Gauge,
    CornerUpLeft,
    ShieldCheck,
];
const HIGHLIGHT = [false, false, false, false, false, true, false, true];

// Tennis-specific tactical metrics (24 total) — proprietary marked with ★
const CATALOG = [
    { label: "S.E.M.", proprietary: true, dim: "Serve Effectiveness Margin" },
    { label: "R.E.M.", proprietary: true, dim: "Return Effectiveness Margin" },
    { label: "P1 (DUE)", proprietary: true, dim: "Determinant Unforced Errors" },
    { label: "Leadership Margin", proprietary: true, dim: "Match control" },
    { label: "Match Quality Score", proprietary: true, dim: "Match-level rating" },
    { label: "Skillset Radar", proprietary: true, dim: "6-axis profile" },
    { label: "%SWP", dim: "Service won %" },
    { label: "%RWP", dim: "Return won %" },
    { label: "%WP", dim: "Total points won %" },
    { label: "%RA / %RM", dim: "Rally effectiveness" },
    { label: "1st Serve In %", dim: "Deuce / Ad split" },
    { label: "2nd Serve Effectiveness", dim: "Deuce / Ad split" },
    { label: "Points won 1st serve", dim: "Deuce / Ad split" },
    { label: "Points won 2nd serve", dim: "Deuce / Ad split" },
    { label: "1st Serve Return In %", dim: "Deuce / Ad split" },
    { label: "2nd Serve Return In %", dim: "Deuce / Ad split" },
    { label: "Return Effectiveness", dim: "Deuce / Ad split" },
    { label: "Rally length distribution", dim: "6 scenarios · 0–15+ shots" },
    { label: "Rally win % by bucket", dim: "Per scenario" },
    { label: "Minimatches", dim: "1st / 2nd serve & return" },
    { label: "LPR — Lost Points in a Row", dim: "Chain analysis" },
    { label: "UER — Unforced Errors in a Row", dim: "Consecutive errors" },
    { label: "Break Point Analysis", dim: "Faced & created" },
    { label: "FH / BH Key Shot Distribution", dim: "Historical" },
];

export default function Dimensions() {
    const { t } = useI18n();
    const d = t.dimensions;
    return (
        <section
            id="product"
            data-testid="dimensions-section"
            className="relative py-24 md:py-32 border-t border-white/5"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                <div className="grid lg:grid-cols-12 gap-8 mb-16 reveal">
                    <div className="lg:col-span-7">
                        <div className="text-xs tracking-[0.22em] uppercase font-bold text-[#D4FF00] mb-4">
                            {d.tag}
                        </div>
                        <h2 className="font-display font-bold uppercase text-4xl md:text-5xl lg:text-6xl tracking-tighter heading-clamp">
                            {d.headline}{" "}
                            <span className="text-[#D4FF00]">{d.headlineAccent}</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 self-end">
                        <p className="text-white/65 text-lg leading-relaxed">{d.sub}</p>
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {d.cards.map((card, i) => {
                        const Icon = ICONS[i];
                        const highlight = HIGHLIGHT[i];
                        return (
                            <div
                                key={card.code}
                                data-testid={`dimension-card-${i}`}
                                className={`card-hover relative rounded-2xl border p-6 reveal group ${
                                    highlight
                                        ? "border-[#D4FF00]/30 bg-gradient-to-br from-[#0E2B1E] to-[#071B12]"
                                        : "border-white/10 bg-[#071B12]"
                                }`}
                                style={{ transitionDelay: `${i * 60}ms` }}
                            >
                                <div className="flex items-center justify-between mb-6">
                                    <div
                                        className={`h-11 w-11 rounded-lg grid place-items-center border ${
                                            highlight
                                                ? "bg-[#D4FF00] border-[#D4FF00] text-[#04140D]"
                                                : "bg-[#04140D] border-white/10 text-[#D4FF00]"
                                        }`}
                                    >
                                        <Icon size={20} />
                                    </div>
                                    <div className="text-[10px] tabular text-white/35 font-mono">
                                        0{i + 1}/08
                                    </div>
                                </div>
                                <div
                                    className={`font-display font-bold uppercase tracking-tight text-base ${
                                        highlight ? "text-[#D4FF00]" : "text-white"
                                    }`}
                                >
                                    {card.code}
                                </div>
                                <div className="mt-1.5 text-sm text-white/80 font-semibold">
                                    {card.tagline}
                                </div>
                                <div className="mt-3 text-[13px] text-white/55 leading-relaxed">
                                    {card.body}
                                </div>
                                {highlight && (
                                    <div className="absolute top-3 right-3 px-2 py-0.5 rounded bg-[#D4FF00]/15 border border-[#D4FF00]/30 text-[#D4FF00] text-[9px] font-bold uppercase tracking-widest">
                                        {d.proprietary}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div data-testid="dimensions-pills" className="mt-10 flex flex-wrap gap-2.5 reveal">
                    {d.pills.map((p) => (
                        <span
                            key={p}
                            className="px-3.5 py-1.5 rounded-full border border-white/10 bg-white/[0.02] text-xs text-white/70 font-medium hover:border-[#D4FF00]/30 hover:text-white transition-colors"
                        >
                            {p}
                        </span>
                    ))}
                </div>

                <div data-testid="metric-catalog" className="mt-12 rounded-2xl border border-white/10 bg-[#071B12] p-6 md:p-8 reveal">
                    <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                        <div>
                            <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#D4FF00] mb-1.5">
                                {d.catalogTag}
                            </div>
                            <div className="font-display font-bold uppercase text-xl md:text-2xl tracking-tight">
                                {d.catalogTitle}
                            </div>
                        </div>
                        <div className="text-xs text-white/55 max-w-md text-right">
                            {d.catalogSub}
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {CATALOG.map((m, i) => (
                            <span
                                key={i}
                                className={`px-3 py-1.5 rounded-md text-[12px] font-mono tracking-tight font-medium border ${
                                    m.proprietary
                                        ? "bg-[#D4FF00]/10 border-[#D4FF00]/40 text-[#D4FF00]"
                                        : "bg-[#04140D] border-white/10 text-white/75 hover:border-white/30"
                                } transition-colors`}
                                title={m.dim}
                            >
                                {m.proprietary && <span aria-hidden>★ </span>}
                                {m.label}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
